import { Box, Center, Flex, Input, InputGroup, Show, Stack, Text } from '@chakra-ui/react';
import { StateEnum, type GroupMember } from '@halo/chat-sdk';
import { SearchIcon, UserItem } from '@halo/ui-chat-react';
import { useTranslation } from 'react-i18next';
import { ContentDrawer } from './ContentDrawer';

interface MembersListProps {
  hideMore?: boolean;
  members: GroupMember[];
  onMemberAvatarClick?: (member: GroupMember) => Promise<void>;
  closeAllClick?: () => void;
}

const MembersList = ({
  members,
  onMemberAvatarClick,
  hideMore,
  closeAllClick,
}: MembersListProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filteredMembers = useMemo(() => {
    const keyword = search?.trim().toLowerCase();

    return members
      .filter((member) => {
        if (!keyword) return true;
        return member.name?.toLowerCase().includes(keyword);
      })
      .sort((a, b) => {
        if (a.state !== b.state) {
          return (a.state ?? 0) - (b.state ?? 0);
        }

        if (a.role !== b.role) {
          return (a.role ?? 0) - (b.role ?? 0);
        }

        return 0;
      });
  }, [members, search]);

  return (
    <Flex
      py="2.5"
      color="fg.muted"
      fontSize="xs"
      fontWeight="medium"
      justifyContent="space-between"
    >
      <Flex gap="0.5">
        <Text>{members.length}</Text>
        <Text>{t('chat.groupMembersText')}</Text>
      </Flex>
      {!hideMore && (
        <ContentDrawer
          title={`${members.length} ${t('chat.groupMembersText')}`}
          trigger={
            <Text color="brand.100" cursor="pointer">
              {t('action.more')}
            </Text>
          }
          closeAllClick={closeAllClick}
        >
          <Stack h="full" gap="2">
            <InputGroup
              startElement={
                <Center flexShrink="0" fontSize="md">
                  <SearchIcon />
                </Center>
              }
            >
              <Input
                size="sm"
                placeholder={t('filter.searchPlaceholder')}
                bg="bg"
                borderRadius="xl"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
            <Box className="scrollHover" borderRadius="xl" bg="bg" flex="1" overflowY="auto">
              {filteredMembers.map((member, index) => (
                <UserItem
                  key={member.user_id}
                  {...member}
                  rippleEffect={false}
                  hoverable={false}
                  onAvatarClick={() => onMemberAvatarClick?.(member)}
                  borderColor={
                    index === filteredMembers.length - 1 ? 'transparent' : 'border.subtle'
                  }
                  userExtraElement={
                    <Show when={member.state !== StateEnum.DELETE_ACCOUNT}>
                      {member.role === 1 && (
                        <Center fontSize="xs" fontWeight="bold">
                          {/* <OWIcon /> */}
                          <Text color="brand.100">{t('chat.groupRoleOwner')}</Text>
                        </Center>
                      )}
                      {member.role === 2 && (
                        <Center fontSize="xs" fontWeight="bold">
                          {/* <MAIcon /> */}
                          <Text color="fg.muted">{t('chat.groupRoleAdmin')}</Text>
                        </Center>
                      )}
                    </Show>
                  }
                />
              ))}
            </Box>
          </Stack>
        </ContentDrawer>
      )}
    </Flex>
  );
};

const MemoizedMembersList = memo(MembersList);
export { MemoizedMembersList as MembersList };
